import { ProductSimple } from '@interfaces/product'
import { Filter } from '@interfaces/filters'

// Utility to check if a metafield value exists
const isFilterInMetafields = (metafields: any, filter: string): boolean => {
  if (!metafields) return false

  return Object.values(metafields).some((metafieldValue) => {
    if (!metafieldValue) return false; // Check for null or undefined

    if (typeof metafieldValue === 'string') {
      return metafieldValue === filter
    }

    if (
      typeof metafieldValue === 'object' &&
      'name' in metafieldValue &&
      typeof metafieldValue.name === 'string'
    ) {
      return metafieldValue.name === filter
    }

    if (Array.isArray(metafieldValue)) {
      return metafieldValue.some(
        (item) =>
          typeof item === 'object' &&
          'name' in item &&
          typeof item.name === 'string' &&
          item.name === filter
      );
    }

    return false
  })
}

const isPriceInRange = (product: ProductSimple, minPrice: number, maxPrice: number): boolean => {
  const productPrice = parseFloat(product.price.replace(/[^0-9.]/g, ''));
  return productPrice >= minPrice && productPrice <= maxPrice;
}

export const filterProducts = (
  products: ProductSimple[],
  selectedFilters: { [key: string]: string[] },
  filters: Filter[],
): ProductSimple[] => {
  if (Object.keys(selectedFilters).length === 0) {
    return products
  }

  return products.filter((product) => {
    return Object.keys(selectedFilters).every((paramName) => {
      const filterValues = selectedFilters[paramName]
      const filter = filters.find((f: Filter) => f.paramName === paramName)
      if (!filter) return false

      const operator = filter.operator

      // Check if the filter is the price range then pass to the isInPriceRange helper function
      if (filter.filterType === 'price_range') {
        const priceRangeStr = filterValues[0];
        if (priceRangeStr) {
          const [min, max] = priceRangeStr.split('-').map(Number);
          return isPriceInRange(product, min, max);
        }
        return true;
      }

      // Check product-level options and metafields
      const inOptions = product.optionsWithValues?.some((option) =>
        option.values?.some((value) => filterValues.includes(value))
      )

      const inProductMetafields = filterValues.some((filterValue) =>
        isFilterInMetafields(product.metafields, filterValue)
      )

      // Check variant-level options and metafields
      const inVariants = product.variants?.some((variant) =>
        filterValues.some((filterValue) => {
          return (
            variant.options.includes(filterValue) ||
            isFilterInMetafields(variant.metafields, filterValue)
          )
        })
      )

      // Combine logic for AND/OR filters
      if (operator === 'AND') {
        return filterValues.every(
          () => inOptions || inProductMetafields || inVariants
        )
      } else if (operator === 'OR') {
        return filterValues.some(
          () => inOptions || inProductMetafields || inVariants
        )
      }
      return false
    })
  })
}

export const sortProducts = (products: ProductSimple[], sortValue: string): ProductSimple[] => {
  return [...products].sort((a, b) => {
    switch (sortValue) {
      case 'manual':
        return 0 // No sorting for manual
      case 'best-selling':
        return (Number(b.unitsSold) || 0) - (Number(a.unitsSold) || 0)
      case 'worst-selling':
        return (Number(a.unitsSold) || 0) - (Number(b.unitsSold) || 0)
      case 'price-ascending':
        return parseFloat(a.price.replace('$', '')) - parseFloat(b.price.replace('$', ''))
      case 'price-descending':
        return parseFloat(b.price.replace('$', '')) - parseFloat(a.price.replace('$', ''))
      case 'created-ascending':
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      case 'created-descending':
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      default:
        return 0
    }
  })
}

export const updateUrl = (newSelectedFilters: { [key: string]: string[] }, newSortValue: string, filters: Filter[], searchUrl?: string | null) => {
  const urlParams = new URLSearchParams(window.location.search)

  // Clear existing filter and sort parameters
  filters.forEach((filter) => {
    urlParams.delete(filter.paramName)
  })
  urlParams.delete('sort_by')

  // Append searchUrl parameters if provided
  if (searchUrl) {
    const searchParams = new URLSearchParams(searchUrl.slice(1))
    searchParams.forEach((value, key) => {
      urlParams.set(key, value)
    })
  }

  // Append new sort value
  urlParams.set('sort_by', newSortValue)

  // Append new selected filters
  Object.keys(newSelectedFilters).forEach((param) => {
    newSelectedFilters[param].forEach((label) => {
      const filter = filters.find((f) => f.paramName === param)
      // Special handling for price range filters
      if (filter?.filterType === 'price_range') {
        // For price range, directly use the range as the URL parameter value
        if (newSelectedFilters[param] && newSelectedFilters[param].length > 0) {
          const priceRange = newSelectedFilters[param][0]; // Get the first value (e.g. "100-200")
          urlParams.set(param, priceRange); // Use the range directly as the value
        }
      }
      const value = filter?.values.find((v) => v.label === label)?.value
      if (value) {
        urlParams.append(param, value)
      }
    })
  })

  window.history.pushState({}, '', `${window.location.pathname}?${urlParams.toString()}`)
}

export const parseUrlParams = (
  filters: Filter[],
): { selectedFilters: { [key: string]: string[] }; checkboxStates: { [key: string]: boolean }; sortValue: string } => {
  const urlParams = new URLSearchParams(window.location.search)
  const selectedFilters: { [key: string]: string[] } = {}
  const checkboxStates: { [key: string]: boolean } = {}

  filters.forEach((filter) => {
    const paramValues = urlParams.getAll(filter.paramName)
    if (paramValues.length > 0) {
      selectedFilters[filter.paramName] = paramValues.map((value) => {
        const filterValue = filter.values.find((f) => f.value === value)
        return filterValue ? filterValue.label : value
      })
      paramValues.forEach((value) => {
        const filterValue = filter.values.find((f) => f.value === value)
        checkboxStates[filterValue ? filterValue.label : value] = true
      })
    }
  })

  const sortValue = urlParams.get('sort_by') || ''

  return { selectedFilters, checkboxStates, sortValue }
}

export const handleFilterChange = (
  filter: string,
  paramName: string,
  selectedFilters: { [key: string]: string[] },
  setSelectedFilters: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>,
  setCheckboxStates: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>,
  sortValue: string,
  filters: Filter[],
  handleClearAll: () => void,
) => {
  let newSelectedFilters: { [key: string]: string[] }
  //Get the filter object to check if it is the price range filter
  const filterObj = filters.find((f) => f.paramName === paramName);
  const isPriceRange = filterObj?.filterType === 'price_range';
  if (selectedFilters[paramName]?.includes(filter)) {
    newSelectedFilters = { ...selectedFilters }
    newSelectedFilters[paramName] = newSelectedFilters[paramName].filter((f) => f !== filter)
    setCheckboxStates((prevStates) => ({
      ...prevStates,
      [filter]: false,
    }))
    if (newSelectedFilters[paramName].length === 0) {
      delete newSelectedFilters[paramName]
    }
  } else {
    newSelectedFilters = { ...selectedFilters }
    if (isPriceRange) {
      // For price range: replace existing values with the new one
      newSelectedFilters[paramName] = [filter];

      // Uncheck any previously checked price range filters
      if (selectedFilters[paramName]) {
        selectedFilters[paramName].forEach(oldFilter => {
          if (oldFilter !== filter) {
            setCheckboxStates(prevStates => ({
              ...prevStates,
              [oldFilter]: false,
            }));
          }
        });
      }
    } else {
      newSelectedFilters[paramName] = [...(newSelectedFilters[paramName] || []), filter]
    }
    setCheckboxStates((prevStates) => ({
      ...prevStates,
      [filter]: true,
    }))
  }
  setSelectedFilters(newSelectedFilters)
  updateUrl(newSelectedFilters, sortValue, filters)

  if (Object.keys(newSelectedFilters).length === 0) {
    handleClearAll()
    return true
  }
  return false
}

export const handleClearAll = (
  setSelectedFilters: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>,
  setCheckboxStates: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>,
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>,
  sortByDefaultValue: string,
  filters: Filter[],
  searchUrl?: string | null
) => {
  setSelectedFilters({})
  setCheckboxStates({})
  setSearchQuery('')
  updateUrl({}, sortByDefaultValue, filters, searchUrl)
}

export const filterProductsBySearch = (products: ProductSimple[], searchQuery: string): ProductSimple[] => {
  return products.filter((product) => {
    const titleMatch = product.title.toLowerCase().includes(searchQuery.toLowerCase())
    const priceMatch =
      product.price.replace('$', '').includes(searchQuery) ||
      product.variants.some((variant) => variant.price.replace('$', '').includes(searchQuery))
    const optionsMatch = product.optionsWithValues.some((option) =>
      (option.values || []).some((value) => value.toLowerCase().includes(searchQuery.toLowerCase())),
    )
    return titleMatch || priceMatch || optionsMatch
  })
}
